<template>
  <v-app>
    <!-- OFFLINE VIEW -->
    <v-container fill-height fluid v-if="!isOnline">
      <v-row>
        <v-col class="d-flex align-center justify-center fade-out">
          <span>It appears that you are offline...</span>
        </v-col>
      </v-row>
      <v-spacer />
    </v-container>

    <!-- POLL RESULTS LOGIN GATE -->
    <!-- When a poll-results link is opened while logged out, show ONLY the login
         modal: no top controls/header, no scrim, uniform background. The dialogs
         below render regardless of this branch. -->
    <div v-else-if="isPollResultsLoginGate" class="poll-login-gate"></div>

    <!-- LOADING ANIMATION -->
    <div v-else-if="isLoading" class="loading-container">
      <div class="music-bars">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>

    <!-- LOAD ERROR VIEW -->
    <!-- Shown when the site's data can't be loaded (e.g. the backend is
         unreachable). Keep it minimal: just the logo and a friendly message. -->
    <v-container
      v-else-if="hasLoadError"
      fill-height
      fluid
      class="load-error-view fade-in"
    >
      <v-row class="load-error-row">
        <v-col class="d-flex flex-column align-center justify-center text-center">
          <img
            alt="Platinum Couch Productions Logo"
            class="load-error-logo prevent-select"
            :src="logo"
          />
          <p
            class="load-error-message"
            :class="{ 'load-error-message--dark': $vuetify.theme.dark }"
          >
            Unable to load the site… Please let the devs know and try again later!
          </p>
        </v-col>
      </v-row>
    </v-container>

    <!-- MAIN CONTENT -->
    <v-container v-else class="pa-0 mt-5 fade-in">
      <!-- TOP CONTROLS - fixed at the top of the window -->
      <div class="top-controls-container">
        <!-- Desktop: Full Top Controls -->
        <top-controls
          v-if="isOnline && !$vuetify.breakpoint.xs"
          @showLogin="isShowingLoginDialog = true"
          @showUpload="isShowingUploadDialog = true"
        />

        <!-- Mobile: Compact Header -->
        <compact-header
          v-if="isOnline && $vuetify.breakpoint.xs"
          @showLogin="isShowingLoginDialog = true"
          @showUpload="isShowingUploadDialog = true"
        />
      </div>

      <!-- LOGO & PLAYER - hidden in video mode on mobile when search bar is open -->
      <v-container
        v-show="!hideVideoForMobileSearch"
        ref="playerContainer"
        class="playerContainer"
        :class="{
          darkBackground: $vuetify.theme.dark,
          lightBackground: !$vuetify.theme.dark,
          'video-mode': isVideoMode,
        }"
        :style="{
          background: $vuetify.theme.themes.dark.background,
          top: computedPlayerContainerTop,
        }"
      >
        <!-- LOGO (Audio Mode, Desktop Only) -->
        <v-row v-if="!isVideoMode && !$vuetify.breakpoint.xs" class="mt-n3 mt-md-n7 prevent-select">
          <img
            alt="Platinum Couch Productions Logo"
            class="mb-0 platinumCouchLogo prevent-select"
            height="167px"
            :src="logo"
            :style="{ opacity: logoOpacity }"
          />
        </v-row>

        <!-- VIDEO PLAYER (Video Mode) - hidden on mobile when search bar is open -->
        <!-- Using v-show to prevent component destruction/recreation which would reset the video -->
        <v-row v-if="isVideoMode" v-show="!hideVideoForMobileSearch" class="mt-n3 mt-md-n7 video-player-row">
          <video-player />
        </v-row>

        <!-- PLAYER -->
        <v-row class="mt-0 player">
          <v-container justify="center">
            <!-- PLAYER CONTROLS (Audio mode only, Desktop only) -->
            <player-controls v-if="!isVideoMode && !$vuetify.breakpoint.xs" />
          </v-container>
        </v-row>
      </v-container>

      <!-- SEARCH BAR (between fixed player and track list) -->
      <div
        v-if="isShowingSearchBar"
        class="search-bar-wrapper"
        :class="{
          darkBackground: $vuetify.theme.dark,
          lightBackground: !$vuetify.theme.dark,
          'video-mode': isVideoMode
        }"
        :style="{
          'margin-top': $vuetify.breakpoint.xs ? undefined : searchBarWrapperMarginTop,
          'top': searchBarTop
        }"
      >
        <v-container>
          <search-bar ref="searchBar" />
        </v-container>
      </div>

      <!-- TRACK LIST / POLL VOTING -->
      <div
        class="trackListContainer prevent-select"
        :style="{ 'margin-top': trackListContainerMarginTop }"
      >
        <poll-results-table
          v-if="isPollResultsMode"
          :sticky-top="playerContainerHeight"
        />
        <poll-vote-list
          v-else-if="isLoggedIn && isPollMode"
          :sticky-top="playerContainerHeight"
        />
        <track-list v-else ref="trackList" @selectionChange="handleSelectionChange" />
      </div>
    </v-container>

    <!-- DIALOGS -->
    <!-- On the poll-results login gate, suppress the scrim and make the dialog
         persistent so it can't be dismissed into an otherwise-empty page. -->
    <login-dialog
      v-model="isShowingLoginDialog"
      :hide-overlay="isPollResultsLoginGate"
      :persistent="isPollResultsLoginGate"
    />
    <upload-dialog v-model="isShowingUploadDialog" />
    <playlist-created-dialog />
    <audio-effects-dialog />
    <comment-dialog />

    <!-- CREATE PLAYLIST BUTTON -->
    <v-row
      v-if="isLoggedIn && isCreatePlaylistMode"
      class="d-flex justify-center align-center createPlaylistButtonContainer py-2 mx-0 px-0"
      :class="{
        darkBackground: $vuetify.theme.dark,
        lightBackground: !$vuetify.theme.dark,
      }"
    >
      <v-btn
        :disabled="!hasSelectedTracks"
        @click="createPlaylist"
        color="secondary"
        class="createPlaylistButton mb-4"
        small
      >
        Create Playlist
      </v-btn>
    </v-row>

    <!-- SNACKBAR -->
    <v-snackbar
      v-model="snackbar.isVisible"
      :timeout="snackbar.timeout"
      content-class="text-center"
    >
      {{ snackbar.text }}
    </v-snackbar>

    <!-- BOTTOM MINI PLAYER (Mobile Only, Audio Mode Only) -->
    <bottom-mini-player v-if="isOnline && !isLoading && !isPollResultsLoginGate && !hasLoadError" />
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PlayerControls from '@/components/player/PlayerControls.vue';
import TopControls from '@/components/player/TopControls.vue';
import CompactHeader from '@/components/player/CompactHeader.vue';
import BottomMiniPlayer from '@/components/player/BottomMiniPlayer.vue';
import TrackList from '@/components/player/TrackList.vue';
import PollVoteList from '@/components/player/PollVoteList.vue';
import PollResultsTable from '@/components/player/PollResultsTable.vue';
import SearchBar from '@/components/player/SearchBar.vue';
import VideoPlayer from '@/components/player/VideoPlayer.vue';
import LoginDialog from '@/components/dialogs/LoginDialog.vue';
import UploadDialog from '@/components/dialogs/UploadDialog.vue';
import PlaylistCreatedDialog from '@/components/dialogs/PlaylistCreatedDialog.vue';
import AudioEffectsDialog from '@/components/dialogs/AudioEffectsDialog.vue';
import CommentDialog from '@/components/player/CommentDialog.vue';

export default {
  name: 'MusicPlayer',

  components: {
    PlayerControls,
    TopControls,
    CompactHeader,
    BottomMiniPlayer,
    TrackList,
    PollVoteList,
    PollResultsTable,
    SearchBar,
    VideoPlayer,
    LoginDialog,
    UploadDialog,
    PlaylistCreatedDialog,
    AudioEffectsDialog,
    CommentDialog,
  },
  
  props: {
    playlistID: String,
    pollResultsHash: String,
  },
  
  data() {
    return {
      isOnline: navigator.onLine,
      isShowingLoginDialog: false,
      isShowingUploadDialog: false,
      hasSelectedTracks: false,
      scrollPosition: 0,
      logoOpacity: 1,
      playerContainerTop: '0px',
      playerTopOffset: 50,
      playerContainerHeight: 0,
      windowWidth: window.innerWidth,
    };
  },
  
  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isLoggedIn',
      isLoading: 'playlist/isLoading',
      playlistLoadError: 'playlist/loadError',
      pollResultsError: 'poll/resultsError',
      isCreatePlaylistMode: 'ui/isCreatePlaylistMode',
      isPollMode: 'ui/isPollMode',
      isPollResultsMode: 'ui/isPollResultsMode',
      snackbar: 'ui/snackbar',
      isVideoMode: 'ui/isVideoMode',
      shouldShowYearLabels: 'ui/shouldShowYearLabels',
      isShowingSearchBar: 'ui/isShowingSearchBar',
    }),

    // Poll-results links require login. While viewing one logged out, we show
    // ONLY the login modal — no top controls, no scrim, no main content.
    isPollResultsLoginGate() {
      return this.isPollResultsMode && !this.isLoggedIn;
    },

    // True when the data needed for the current view couldn't be loaded (e.g.
    // the backend is unreachable). On a poll-results page that's the results
    // fetch; otherwise it's the playlist/recordings fetch.
    hasLoadError() {
      return this.isPollResultsMode ? this.pollResultsError : this.playlistLoadError;
    },

    logo() {
      return this.$vuetify.theme.dark
        ? require('../assets/logo dark theme.png')
        : require('../assets/logo.png');
    },

    // Hide video player on mobile when search bar is open to avoid iOS Safari layout issues
    hideVideoForMobileSearch() {
      return this.$vuetify.breakpoint.xs && this.isShowingSearchBar && this.isVideoMode;
    },

    computedPlayerContainerTop() {
      // Mobile video mode: position below compact header
      if (this.$vuetify.breakpoint.xs && this.isVideoMode) {
        return '36px';  // Height of compact header in video mode
      }
      // Desktop or audio mode: use scroll-based position
      return this.playerContainerTop;
    },

    searchBarWrapperMarginTop() {
      // Position search bar right after the fixed player container
      // playerContainerHeight now represents rect.bottom which already accounts for scroll
      const playerBottom = this.playerContainerHeight || 300;
      return playerBottom + 'px';
    },

    searchBarTop() {
      // Mobile only: Calculate top position for fixed search bar
      if (!this.$vuetify.breakpoint.xs) {
        return undefined;
      }

      // Both video mode and audio mode: position below compact header
      // In video mode, the video player is hidden when search bar is open,
      // so positioning works the same as audio mode
      return '95px';
    },

    trackListContainerMarginTop() {
      // Mobile (xs breakpoint): Use CompactHeader + spacingBuffer
      if (this.$vuetify.breakpoint.xs && !this.isVideoMode) {
        // On mobile, we have CompactHeader (~93px actual) instead of player container
        const compactHeaderHeight = 75;  // Reduced by ~20px for tighter layout

        if (!this.isShowingSearchBar) {
          // Search hidden: minimal spacing for tight layout
          const spacingBuffer = 1;
          return (compactHeaderHeight + spacingBuffer) + 'px';  // 76px total
        }

        // Search bar visible: account for header + search bar height + spacing
        // Search bar is fixed at top: 95px, with ~50px height
        const searchBarHeight = 50;
        const spacingBuffer = 4;
        return (compactHeaderHeight + searchBarHeight + spacingBuffer) + 'px';  // 129px total
      }

      // Mobile video mode: when search bar is open, video is hidden, so use same margins as audio mode
      if (this.$vuetify.breakpoint.xs && this.isVideoMode) {
        const compactHeaderHeight = 75;

        if (this.isShowingSearchBar) {
          // Video is hidden, search bar is visible - same as audio mode
          const searchBarHeight = 50;
          const spacingBuffer = 4;
          return (compactHeaderHeight + searchBarHeight + spacingBuffer) + 'px';  // 129px total
        }

        // Search bar hidden, video is visible - use relative positioning
        return '0px';
      }

      // Desktop: Original logic
      // Responsive spacing - tighter on small screens
      // Container components add ~20px of their own margin/padding
      let spacingBuffer;
      let searchBarSpacing;

      if (this.windowWidth <= 420) {
        // Small screens: very tight spacing (accounting for container margins + search bar internal padding)
        spacingBuffer = -15;
        searchBarSpacing = 12; // Add enough to clear search bar internal elements
      } else if (this.windowWidth <= 820) {
        // Medium screens: moderate spacing
        spacingBuffer = -10;
        searchBarSpacing = 12;
      } else {
        // Large screens: comfortable spacing
        spacingBuffer = -5;
        searchBarSpacing = 12;
      }

      if (!this.isShowingSearchBar) {
        // Search bar not visible - track list needs to clear the entire fixed player
        const playerBottom = this.playerContainerHeight > 0 ? this.playerContainerHeight : 0;

        // If not measured yet, return a minimal value and let the interval update it
        if (playerBottom === 0) {
          return '300px'; // Temporary fallback
        }

        return (playerBottom + spacingBuffer) + 'px';
      }

      // Search bar is visible and positioned after player
      // Track list just needs small buffer after search bar
      return searchBarSpacing + 'px';
    },
  },

  watch: {
    // Poll results link: gate on login, then load results (logged-in users only)
    pollResultsHash: {
      immediate: true,
      handler(hash) {
        this.$store.dispatch('ui/setPollResultsMode', !!hash);
        if (hash) {
          if (this.isLoggedIn) {
            this.$store.dispatch('poll/fetchResults', hash).catch(() => {});
          } else {
            this.isShowingLoginDialog = true;
          }
        }
      },
    },
  },

  created() {
    // Prevent iOS Safari from restoring a previous scroll position on page load
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    if (this.playlistID) {
      this.$store.commit('playlist/SET_PLAYLIST_ID', this.playlistID);
      const playlistURL = process.env.VUE_APP_BASE_URL + '/' + this.playlistID;
      this.$store.commit('playlist/SET_PLAYLIST_URL', playlistURL);
    }

    if (window.innerHeight < 844) {
      window.addEventListener('scroll', this.handleScroll);
    }
  },
  
  mounted() {
    // Set up online/offline event listeners
    window.addEventListener('online', () => {
      this.isOnline = true;
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Set up audio monitoring interval
    this._monitorInterval = setInterval(() => {
      this.$store.dispatch('player/monitorTrackPlayback');
    }, 4000);

    // Player container measurement: a ResizeObserver replaces the old
    // 200ms forever-poll (which forced a layout read 5x/second for the
    // lifetime of the page). The observer fires on real size changes;
    // scroll-driven position changes are handled in handleScroll.
    const attachPlayerObserver = () => {
      if (this._playerResizeObserver) return;
      try {
        let el = null;
        if (this.$refs.playerContainer) {
          el = this.$refs.playerContainer.$el || this.$refs.playerContainer;
        }
        if (!el && typeof document !== 'undefined') {
          el = document.querySelector('.playerContainer');
        }
        if (el && typeof ResizeObserver === 'function') {
          this._playerResizeObserver = new ResizeObserver(() => {
            this.updatePlayerContainerHeight();
          });
          this._playerResizeObserver.observe(el);
          this.updatePlayerContainerHeight();
        }
      } catch (e) {
        // Fall back to the event-driven remeasures below
      }
    };
    this._attachPlayerObserver = attachPlayerObserver;
    attachPlayerObserver();

    // Initialize player after recordings are loaded
    this.$watch(
      () => this.$store.getters['playlist/isLoading'],
      (isLoading) => {
        if (!isLoading && this.$store.getters['playlist/recordings'].length > 0) {
          this.$store.dispatch('player/initializePlayer');

          // The main content (and .playerContainer) only exists once
          // loading finishes — attach the observer then.
          this.$nextTick(() => attachPlayerObserver());

          // Output test data for automated testing
          // console.log('=== TEST DATA START ===');
          // console.log(JSON.stringify({
          //   recordings: this.$store.getters['playlist/recordings'],
          //   videos: this.$store.getters['video/videos'],
          //   isLoggedIn: this.$store.getters['auth/isLoggedIn'],
          // }, null, 2));
          // console.log('=== TEST DATA END ===');
        }
      }
    );

    // Measure player container height initially (multiple times to ensure accuracy)
    this.$nextTick(() => {
      this.updatePlayerContainerHeight();
      setTimeout(() => this.updatePlayerContainerHeight(), 50);
      setTimeout(() => this.updatePlayerContainerHeight(), 100);
      setTimeout(() => this.updatePlayerContainerHeight(), 200);
      setTimeout(() => this.updatePlayerContainerHeight(), 500);
    });

    // Scroll to top on initial page load (fixes iOS Safari showing page slightly scrolled).
    // Must fire after playerContainerHeight measurements stabilize, since margin-top
    // changes on the track list can shift content and invalidate earlier scrollTo calls.
    this.$nextTick(() => {
      window.scrollTo(0, 0);
      setTimeout(() => window.scrollTo(0, 0), 100);
      setTimeout(() => window.scrollTo(0, 0), 300);
      setTimeout(() => window.scrollTo(0, 0), 600);
    });

    // Update on window resize
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
      this.updatePlayerContainerHeight();
      setTimeout(() => this.updatePlayerContainerHeight(), 100);
    });

    // Watch for video mode changes and remeasure
    this.$watch('isVideoMode', (isVideoMode) => {
      // Scroll to top when entering video mode
      if (isVideoMode) {
        window.scrollTo(0, 0);
      }

      // Multiple measurements to ensure video player has loaded
      this.$nextTick(() => {
        this.updatePlayerContainerHeight();
      });
      setTimeout(() => {
        this.updatePlayerContainerHeight();
      }, 100);
      setTimeout(() => {
        this.updatePlayerContainerHeight();
      }, 500);
      setTimeout(() => {
        this.updatePlayerContainerHeight();
      }, 1000);
    });

    // Watch for search bar changes and remeasure
    this.$watch(() => this.$store.getters['ui/isShowingSearchBar'], () => {
      // Add a delay to ensure search bar has fully rendered/animated
      setTimeout(() => {
        this.$nextTick(() => {
          this.updatePlayerContainerHeight();
        });
      }, 100);
    });

    // Watch for when data is loaded and remeasure.
    // Non-deep on purpose: SET_RECORDINGS/REMOVE_RECORDING replace the array
    // reference, which is all we need — a deep watcher would re-walk all
    // ~170 recording objects on every mutation.
    this.$watch(() => this.$store.getters['playlist/recordings'], () => {
      setTimeout(() => {
        this.$nextTick(() => {
          this.updatePlayerContainerHeight();
        });
      }, 100);
    });

    // When the user logs in on a poll-results link, load the results
    this.$watch(() => this.$store.getters['auth/isLoggedIn'], (loggedIn) => {
      if (loggedIn && this.pollResultsHash) {
        this.isShowingLoginDialog = false;
        this.$store.dispatch('poll/fetchResults', this.pollResultsHash).catch(() => {});
      }
    });

    // A playlist link that doesn't exist (404): the store has already fallen back to
    // the home playlist and shown a snackbar. Move the URL back to "/" so the controls
    // match what's on screen and a refresh doesn't replay the dead link.
    this.$watch(() => this.$store.getters['playlist/notFoundPlaylistID'], (notFoundID) => {
      if (notFoundID && this.$router && this.$route && this.$route.path !== '/') {
        const navigation = this.$router.replace('/');
        if (navigation && typeof navigation.catch === 'function') {
          navigation.catch(() => {});
        }
      }
    });

    // Watch for video playing state and close search bar when video starts
    this.$watch(() => this.$store.getters['video/isPlaying'], (isPlaying) => {
      if (isPlaying && this.isVideoMode && this.isShowingSearchBar) {
        this.$store.dispatch('ui/toggleSearchBar');
      }
    });

    // (The old 200ms "continuous remeasurement" interval is intentionally
    // gone — the ResizeObserver above plus the event-driven remeasures
    // cover every case it papered over, without polling.)
  },
  
  beforeDestroy() {
    // Remove event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.updatePlayerContainerHeight);
    window.removeEventListener('online', () => {
      this.isOnline = true;
    });
    window.removeEventListener('offline', () => {
      this.isOnline = false;
    });

    // Clear playback monitor interval
    if (this._monitorInterval) {
      clearInterval(this._monitorInterval);
    }

    // Disconnect the player container ResizeObserver
    if (this._playerResizeObserver) {
      this._playerResizeObserver.disconnect();
      this._playerResizeObserver = null;
    }
  },
  
  methods: {
    ...mapActions({
      createPlaylistAction: 'playlist/createPlaylist',
    }),

    updatePlayerContainerHeight() {
      try {
        // Try multiple ways to get the element
        let containerElement = null;

        if (this.$refs.playerContainer) {
          // Vuetify component - access via $el
          containerElement = this.$refs.playerContainer.$el || this.$refs.playerContainer;
        }

        if (!containerElement) {
          // Fallback: query directly
          containerElement = document.querySelector('.playerContainer');
        }

        if (containerElement && containerElement.getBoundingClientRect) {
          requestAnimationFrame(() => {
            const rect = containerElement.getBoundingClientRect();
            const newHeight = Math.ceil(rect.bottom);

            // Only update if we got a valid measurement
            if (newHeight > 0) {
              this.playerContainerHeight = newHeight;
            }
          });
        }
      } catch (e) {
        // Silently fail - the interval will retry
      }
    },

    handleScroll() {
      // rAF-throttle: scroll events can fire far faster than frames render,
      // and this handler mutates layout-affecting styles. One update per
      // frame is all the screen can show anyway.
      if (this._scrollRafPending) return;
      this._scrollRafPending = true;

      requestAnimationFrame(() => {
        this._scrollRafPending = false;

        if (this.$store.getters['playlist/recordings'].length < 10 ||
            this.$store.getters['ui/isUserTyping']) {
          return;
        }

        const scrollY = window.scrollY;

        if (scrollY < this.playerTopOffset) {
          const newPosition = 0 - scrollY;
          this.scrollPosition = scrollY * 1.5;
          this.playerContainerTop = newPosition + 'px';
          this.logoOpacity = 1 - (scrollY * 1.7) / 100;
        } else {
          this.playerContainerTop = -this.playerTopOffset + 'px';
          this.logoOpacity = 0;
        }

        // The container is fixed and its `top` just moved — its bottom edge
        // (used for track-list margins) moved with it. The ResizeObserver
        // can't see position changes, so remeasure here.
        this.updatePlayerContainerHeight();
      });
    },
    
    createPlaylist() {
      // Get selected tracks from TrackList component
      const trackListRef = this.$refs.trackList;
      if (trackListRef) {
        trackListRef.submitCreatePlaylist();
      }
    },
    
    handleSelectionChange(hasSelectedTracks) {
      this.hasSelectedTracks = hasSelectedTracks;
    },
  }
};
</script>

<style>
html,
body {
  overscroll-behavior-y: none;
}

.top-controls-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10000;
}

.deactivateButtonBugFix:focus::before {
  opacity: 0 !important;
}

.fade-in {
  opacity: 1;
  animation-name: fadeInOpacity;
  animation-iteration-count: 1;
  animation-timing-function: ease-in;
  animation-duration: 0.5s;
}

.container {
  max-width: 800px !important;
}

.platinumCouchLogo {
  margin: 0 auto;
  margin-top: 105px;
}

.playerContainer {
  position: fixed;
  width: 100%;
  z-index: 999;
  pointer-events: auto;
  overflow: visible;
}

/* Mobile video mode: Use relative positioning for natural document flow
   This fixes iOS Safari layout issues with keyboard */
@media only screen and (max-width: 599px) {
  .playerContainer.video-mode {
    position: relative;
    top: auto !important;  /* Override dynamic top style */
    z-index: 1;
    margin-top: 36px;  /* Space for compact header */
  }
}

.search-bar-wrapper {
  width: 100%;
  z-index: 998;
  padding: 0;
  position: relative;
}

/* Mobile: Position search bar below compact header */
@media only screen and (max-width: 599px) {
  .search-bar-wrapper {
    position: fixed;
    /* top is set dynamically via :style binding */
    left: 0;
    right: 0;
    z-index: 10001;  /* Above compact header (10000) */
  }
  /* Video mode search bar: same as audio mode (fixed at top)
     Video player is hidden when search bar is open to avoid iOS Safari layout issues */
}

/* Reduce container padding inside search bar wrapper */
.search-bar-wrapper .container {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.trackListContainer {
  margin-bottom: 150px;
  width: 100%;
}

.createPlaylistButtonContainer {
  position: fixed;
  bottom: 0px;
  width: 100%;
  background-color: var(--v-primary-base);
}

/* On mobile, position above the bottom mini player */
@media only screen and (max-width: 599px) {
  .createPlaylistButtonContainer {
    bottom: 130px;
  }
}

.lightBackground {
  background: white;
}

.darkBackground {
  background: #121212;
}

.prevent-select {
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}

.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Poll-results login gate: empty full-height area so the app background is
   uniform behind the (scrim-less) login modal. */
.poll-login-gate {
  min-height: 100vh;
  width: 100%;
}

/* Load error view: centered logo + message shown when data can't be loaded. */
.load-error-view {
  min-height: 100vh;
}

.load-error-row {
  width: 100%;
}

.load-error-logo {
  width: 260px;
  max-width: 70vw;
  height: auto;
  margin-bottom: 24px;
}

.load-error-message {
  font-size: 1.1rem;
  line-height: 1.5;
  max-width: 420px;
  margin: 0 auto;
  padding: 0 16px;
  color: #121212;
}

.load-error-message--dark {
  color: #efefef;
}

@media only screen and (max-width: 600px) {
  .load-error-logo {
    width: 180px;
  }
  .load-error-message {
    font-size: 1rem;
  }
}

.music-bars {
  display: flex;
  align-items: flex-end;
  gap: 5px;
  height: 48px;

  span {
    width: 8px;
    height: 100%;
    background-color: var(--v-primary-base);
    /* Half the bar width -> perfect capsule ends at any height */
    border-radius: 4px;
    /* Animate height (not transform: scaleY) — scaling squashes the
       border-radius vertically, which is what made the bar ends look
       flattened at the low points of the bounce. Height animation keeps
       the rounded caps geometrically perfect at every frame. */
    animation: bounce 2.8s ease infinite alternate;

    &:nth-of-type(1) {
      animation-delay: -1.2s;
    }
    &:nth-of-type(2) {
      animation-delay: -2.8s;
    }
    &:nth-of-type(3) {
      animation-delay: -3.7s;
    }
    &:nth-of-type(4) {
      animation-delay: -2.1s;
    }
    &:nth-of-type(5) {
      animation-delay: -0.5s;
    }
  }
}

@keyframes bounce {
  10% {
    height: 30%;
  }
  30% {
    height: 100%;
  }
  60% {
    height: 50%;
  }
  80% {
    height: 75%;
  }
  100% {
    height: 60%;
  }
}

@keyframes fadeInOpacity {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.fade-out {
  opacity: 1;
  animation-name: fadeOutOpacity;
  animation-iteration-count: 1;
  animation-timing-function: ease-in;
  animation-duration: 1s;
}

@keyframes fadeOutOpacity {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@media only screen and (max-width: 1270px) {
  .platinumCouchLogo {
    margin-top: 40px;
  }
}

/* 960px aka md */
@media only screen and (max-width: 960px) {
  .platinumCouchLogo {
    margin-top: 30px;
  }
}

@media only screen and (max-width: 650px) {
  .platinumCouchLogo {
    height: 160px;
    width: 242px;
  }
}

@media only screen and (max-width: 550px) {
  .platinumCouchLogo {
    height: 120px;
    width: 182px;
  }
}

@media only screen and (max-width: 450px) {
  .platinumCouchLogo {
    height: 90px;
    width: 135px;
  }
}
</style>